import { select } from '@pressreader/appstore';
import { AnyPurchaseProductDto, AvailableProductsDto, mapAvailableProductsDto, mapIssuePurchaseInfo } from '@pressreader/commerce-api';
import {
    AnyIssueInfoContentRef,
    IIssueInfo,
    IIssueInfoArticleRef,
    IIssueInfoPage,
    IIssueInfoPageArticle,
    IIssueInfoPageKey,
    IIssueInfoSectionRef,
    type IPublicationContentRestrictions,
    IssueInfoSection,
} from '@pressreader/content-types';
import { extractCidDateKeyString, extractDate, resolveArticleGroupId, setReplicaPageImageMeta } from '@pressreader/content-utils';
import { ISize } from '@pressreader/geometry';
import { getSponsorshipStatus } from '@pressreader/sponsorship-core';
import { SponsorshipStatus } from '@pressreader/sponsorship-types';
import { NullableFields } from '@pressreader/types';

import type { IssueInfoDto } from '../types';
import type { IIssuePageKeyDto, IIssueTocArticleDto, IIssueTocDto, IIssueTocPageDto } from '../types/issue.layout';

function mapIssuePageKeyDto(dto: IIssuePageKeyDto): IIssueInfoPageKey | null {
    if (!dto.Key) {
        return null;
    }

    return {
        key: dto.Key,
        confirmationRequired: dto.ConfirmationRequired,
    };
}

function mapIssuePageArticleDto(dto: IIssueTocArticleDto): IIssueInfoPageArticle {
    return {
        id: String(dto.Id),
        primaryArticleId: '', // let it be empty as it's not correct to always use dto.Id as primaryArticleId
        regionId: dto.RegionId,
        title: dto.Title,
        text: dto.Text,
        byline: dto.Byline,
        rate: dto.Rate,
        type: dto.Type,
        contFromPage: dto.ContFromPage,
        contFromReg: dto.ContFromReg,
        contOnPage: dto.ContOnPage,
        contOnReg: dto.ContOnReg,
        image: dto.Image && { height: dto.Image.Height, width: dto.Image.Width, key: dto.Image.Key, uid: dto.Image.Uid },
    };
}

function mapIssueInfoPageFromDto(dto: IssueInfoDto, pageDto: IIssueTocPageDto, pageKeyDto?: IIssuePageKeyDto): IIssueInfoPage {
    const isRtl = dto.Newspaper.IsRightToLeft;

    const { PageNumber: pageNumber } = pageDto;

    const idx = pageNumber - 1;
    const isLast = pageNumber === dto.Pages;
    const single = idx === 0 || (isLast && idx % 2 === 1);

    const spread: IIssueInfoPage['spread'] = single ? null : idx % 2 === 1 ? (isRtl ? 'right' : 'left') : isRtl ? 'left' : 'right';

    const pageInfo = dto.PagesInfo.find(p => p.PageNumber === pageNumber);

    const pageKey = pageKeyDto ? mapIssuePageKeyDto(pageKeyDto) : null;

    const pageLocked = pageNumber > 1 && !pageKey && !(select(getSponsorshipStatus) === SponsorshipStatus.On) && !dto.SubscriptionsInfo.IsFreeContent;
    // isLocked: pageNumber > 1 && dto.SubscriptionsInfo.IsFreeContent === false,
    // has been changed to use IsFreeContent instead of CouldPotentiallyBeRead cause of backend has been changed :
    // the new backend returns CouldPotentiallyBeRead === true when the item is available for reading by the current user
    // OR when an item can be obtained using the current subscription balance (without any explicit purchase)
    //
    //  at the same time, the SubscriptionsInfo?.IsFreeContent property returns true when the item could be read by the current user
    //  without any additional confirmation or purchase

    return {
        pageNumber,
        pageName: pageDto.PageName,

        expungeVersion: pageInfo?.ExpungeVersion ?? 0,

        sectionName: pageDto.SectionName,
        unhyphenatedSectionName: pageDto.UnhyphenatedSectionName,

        spread,

        size: { width: pageDto.Width, height: pageDto.Height },
        pageKey,
        isLocked: pageLocked,

        imageMeta: null,

        articles: pageDto.Articles?.map(mapIssuePageArticleDto) || [],
    };
}

function setPrimaryArticleId(article: IIssueInfoPageArticle, articleByRegionIdMap: Map<number, IIssueInfoPageArticle>) {
    if (article.primaryArticleId) {
        return;
    }

    if (!article.contFromReg && !article.contOnReg) {
        article.primaryArticleId = article.id;
        return;
    }

    let primaryArticleId = article.id;
    const continuations = new Map<number, IIssueInfoPageArticle>();
    const trackContinuations = (cur: IIssueInfoPageArticle | undefined) => {
        if (!cur || continuations.has(cur.regionId)) {
            return;
        }

        continuations.set(cur.regionId, cur);
        if (cur.primaryArticleId) {
            primaryArticleId = cur.primaryArticleId;
        }

        trackContinuations(articleByRegionIdMap.get(cur.contOnReg || 0));
        trackContinuations(articleByRegionIdMap.get(cur.contFromReg || 0));
    };

    trackContinuations(article);
    continuations.forEach(a => {
        a.primaryArticleId = primaryArticleId;
    });
}

function mapIssueInfoPages(issueDto: IssueInfoDto, tocDto: IIssueTocDto) {
    const pages: IIssueInfoPage[] = [];
    const refs: IIssueInfoArticleRef[] = [];

    const articleByRegionIdMap = new Map<number, IIssueInfoPageArticle>();

    for (const tocPage of tocDto.Pages || []) {
        const page = mapIssueInfoPageFromDto(issueDto, tocPage);

        for (const article of page.articles) {
            articleByRegionIdMap.set(article.regionId, article);
        }

        pages.push(page);
    }

    let currSectionName: string | undefined;
    let currSectionId = '';
    const sections: IssueInfoSection[] = [];
    for (const page of pages) {
        if (currSectionName !== page.sectionName) {
            currSectionName = page.sectionName;
            currSectionId = formatSectionId(page.pageNumber);
            sections.push({ id: currSectionId, name: currSectionName, pages: [page.pageNumber], hasArticles: !!page.articles.length });
        } else {
            const section = sections[sections.length - 1];
            section.pages.push(page.pageNumber);
            section.hasArticles = section.hasArticles || !!page.articles.length;
        }
        for (const article of page.articles) {
            // Doing it after all articles are added to the map.
            setPrimaryArticleId(article, articleByRegionIdMap);

            processPageArticles(article, page, currSectionId, refs);
        }
    }

    setReplicaPageImageMeta(buildImageMeta(issueDto), pages);

    return { pages, sections, refs };
}

function formatSectionId(pageNumber: number): string {
    return `section_${pageNumber}`;
}

function processPageArticles(article: IIssueInfoPageArticle, page: IIssueInfoPage, sectionId: string, refs: IIssueInfoArticleRef[]) {
    const articleGroupId = resolveArticleGroupId(article);
    const ref = refs.find(r => r.sectionId === sectionId && r.primaryArticleId === articleGroupId);
    if (ref) {
        ref.continuationRegions.push(article.regionId);
    } else {
        refs.push(createArticleRef(sectionId, page, article));
    }
}

function createArticleRef(sectionId: string, page: IIssueInfoPage, article: IIssueInfoPageArticle): IIssueInfoArticleRef {
    const articleGroupId = resolveArticleGroupId(article);

    return {
        sectionId,
        sectionName: page.sectionName,
        pageNumber: page.pageNumber,
        id: article.id,
        primaryArticleId: articleGroupId,
        regionId: article.regionId,
        continuationRegions: [],
    };
}

function buildImageMeta(issueDto: IssueInfoDto) {
    const maxFirstPageSize: ISize = {
        width: issueDto.MaxUnrestrictedFirstPageSize.W,
        height: issueDto.MaxUnrestrictedFirstPageSize.H,
    };

    const maxNonFirstPageSize: ISize = {
        width: issueDto.MaxUnrestrictedPageSize.W,
        height: issueDto.MaxUnrestrictedPageSize.H,
    };

    const allowedNonTiledSizes: NullableFields<ISize, 'width'>[] = issueDto.Heights.map(h => ({ width: null, height: h }));
    const allowedTiledSizes: ISize[] = issueDto.MagnifierPageSizes.map(s => ({ width: s.W, height: s.H }));

    const singleLayer = issueDto.Issue.ImagesEngineVersion === 1;

    return { singleLayer, maxFirstPageSize, maxNonFirstPageSize, allowedNonTiledSizes, allowedTiledSizes };
}

function extractContentRefs(articleRefs: IIssueInfoArticleRef[]): AnyIssueInfoContentRef[] {
    let prevSectionId: string | null = null;
    let sectionIdx = -1;
    let sectionRef: IIssueInfoSectionRef | null = null;

    const result: AnyIssueInfoContentRef[] = [];
    for (const ref of articleRefs) {
        if (ref.sectionId !== prevSectionId) {
            ++sectionIdx;
            sectionRef = createSectionRef(ref, sectionIdx);
            result.push(sectionRef);
            prevSectionId = ref.sectionId;
        }
        if (sectionRef && !sectionRef.pageNumbers.includes(ref.pageNumber)) {
            sectionRef.pageNumbers.push(ref.pageNumber);
        }
        result.push(ref);
    }

    return result;
}

function createSectionRef({ sectionId, sectionName }: IIssueInfoArticleRef, sectionIdx: number): IIssueInfoSectionRef {
    return {
        sectionId,
        sectionIdx,
        sectionName,
        pageNumbers: [],
    };
}

/**
 * WARNING: this function returns article metadata with a chance of
 * primary article id being incorrect.
 */
function mapIssueInfoDto(
    dto: IssueInfoDto,
    tocDto: IIssueTocDto,
    productDtos: AnyPurchaseProductDto[],
    availableProductsDtos: AvailableProductsDto,
): IIssueInfo {
    const issueKey = dto.Issue.Issue;
    const issueCidDateKey = extractCidDateKeyString(issueKey);
    const issueDate = extractDate(issueKey);

    const { pages, sections, refs } = mapIssueInfoPages(dto, tocDto);

    return {
        issueKey,
        issueCidDateKey: issueCidDateKey,
        issueId: dto.Issue.IssueId,

        date: issueDate,
        issueVer: tocDto.Meta.IssueVer,
        expungeVersion: dto.Issue.ExpungeVersion,
        issueVersion: tocDto.Meta.IssueVersion,
        couldBeRead: dto.CouldPotentiallyBeRead,
        numPages: dto.Pages,

        rtl: dto.Newspaper.IsRightToLeft,

        newspaper: {
            cid: dto.Issue.CID,
            name: dto.Newspaper.Name,
            mastheads: dto.Newspaper.Mastheads
                ? {
                      whiteImageId: dto.Newspaper.Mastheads.WhiteImageId,
                      colorImageId: dto.Newspaper.Mastheads.ColorImageId,
                      width: dto.Newspaper.Mastheads.Width,
                      height: dto.Newspaper.Mastheads.Height,
                  }
                : null,
            isoLanguage: dto.Newspaper.ISOLanguage,
            locale: dto.Newspaper.Culture,
        },

        slugs: dto.Slugs ? { country: dto.Slugs.Country, newspaper: dto.Slugs.Newspaper } : { country: '', newspaper: '' },

        pages,
        articleRefs: refs,
        contentRefs: extractContentRefs(refs),
        sections,

        layout: {
            version: dto.Layout.LayoutVersion,
        },

        smartFlow: {
            layoutValid: dto.Layout.ValidForSmartFlow,
            enabled: dto.Newspaper.EnableSmart,
        },

        availableProducts: mapAvailableProductsDto(availableProductsDtos),
        purchaseInfo: mapIssuePurchaseInfo(dto.SubscriptionsInfo, productDtos),

        sponsorInfo: dto.Sponsor
            ? { name: dto.Sponsor.Name, logoUrl: dto.Sponsor.LogoUrl, broughtBy: dto.Sponsor.BroughtBy || dto.Sponsor.Name }
            : null,

        restrictions: {
            rateLimitsInfo: {
                hasReachedIssueReadLimit: dto.Restrictions?.RateLimitsInfo?.HasReachedIssueReadLimit ?? false,
                hasReachedDailyIssueReadLimit: dto.Restrictions?.RateLimitsInfo?.HasReachedDailyIssueReadLimit ?? false,
                hasIssueReadLimitReachingWarning: dto.Restrictions?.RateLimitsInfo?.HasIssueReadLimitReachingWarning ?? false,
            },
        },
    };
}

function mapIssueInfoDtoRestrictions(dto: IssueInfoDto): IPublicationContentRestrictions {
    const restrictions = dto.Restrictions;
    return {
        sound: restrictions?.Sound ?? false,
        blogging: restrictions?.Blogging ?? false,
        comments: restrictions?.Comments ?? false,
        emailSharing: restrictions?.EmailSharing ?? false,
        readItLater: restrictions?.ReadItLater ?? false,
        smartFlow: restrictions?.SmartFlow ?? false,
        translation: restrictions?.Translation ?? false,
        anonymousContentRestricted: restrictions?.Blackout ?? false,
    };
}

export { extractContentRefs, formatSectionId, mapIssueInfoDto, mapIssueInfoDtoRestrictions, mapIssuePageKeyDto };
